import { useCallback, useEffect, useRef, type RefObject } from 'react';

// Covers text, quote, link and attachment children through row capture, while
// keeping a short tap and intentional text selection as ordinary operations.
export default function useChatMessageInteraction({ selection, message, rowRef, onContextMenu }: {
  selection: any; message: any; rowRef: RefObject<HTMLDivElement>; onContextMenu?: ((event: any, message: any) => void) | null;
}) {
  const press = useRef<any>(null);
  const suppressClick = useRef(false);
  const latest = useRef<any>(null);
  const eligible = Boolean(selection?.eligible(message));
  const selecting = Boolean(selection?.active);
  const cancelPress = useCallback(() => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
    window.removeEventListener('scroll', cancelPress, true);
  }, []);
  const openOriginalMenu = (position?: { clientX: number; clientY: number }) => {
    const rect = rowRef.current?.getBoundingClientRect();
    onContextMenu?.(new MouseEvent('contextmenu', { clientX: position?.clientX ?? rect?.left ?? 0, clientY: position?.clientY ?? rect?.top ?? 0 }), selectionMessage);
  };
  const selectionMessage = message ? { ...message, openMessageMenu: openOriginalMenu } : null;
  latest.current = { selection, selectionMessage, eligible, selecting, openOriginalMenu };
  useEffect(() => cancelPress, [cancelPress]);
  return {
    message: selectionMessage,
    handlers: {
    onKeyDown: (event: any) => {
      if (event.target !== event.currentTarget || selection?.busy) return;
      if (event.key === ' ' && eligible) {
        event.preventDefault();
        if (selecting) selection.toggle(selectionMessage); else openOriginalMenu();
      } else if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
        if (selecting) { event.preventDefault(); if (eligible) selection.longPress(selectionMessage); }
        else { event.preventDefault(); openOriginalMenu(); }
      }
    },
    onContextMenu: (event: any) => {
      if (suppressClick.current) { event.preventDefault(); event.stopPropagation(); return; }
      cancelPress();
      if (selecting) { event.preventDefault(); event.stopPropagation(); if (eligible) selection.longPress(selectionMessage); }
      else onContextMenu?.(event, selectionMessage);
    },
    onPointerDownCapture: (event: any) => {
      suppressClick.current = false;
      cancelPress();
      if (event.button !== 0 || (selecting ? !eligible : !onContextMenu)) return;
      const target = event.target as Element;
      const selectionSlot = target.closest('.chat-topic-selection-slot');
      if (!target.closest('.chat-message-row__bubble, .chat-topic-selection-slot') || (!selectionSlot && target.closest('input, textarea, select')) || target.closest('[role="menu"], [data-no-message-selection]')) return;
      window.addEventListener('scroll', cancelPress, true);
      const x = event.clientX, y = event.clientY;
      press.current = { x, y, timer: setTimeout(() => {
        if (window.getSelection()?.toString()) return;
        suppressClick.current = true;
        const current = latest.current;
        if (current.selecting && current.eligible) current.selection.longPress(current.selectionMessage);
        else if (!current.selecting) current.openOriginalMenu({ clientX: x, clientY: y });
      }, 500) };
    },
    onPointerMoveCapture: (event: any) => {
      if (press.current && Math.hypot(event.clientX - press.current.x, event.clientY - press.current.y) > 8) cancelPress();
    },
    onPointerUpCapture: cancelPress,
    onPointerCancelCapture: cancelPress,
    onClickCapture: (event: any) => {
      if (suppressClick.current) { event.preventDefault(); event.stopPropagation(); suppressClick.current = false; return; }
      if ((event.target as Element).closest('.chat-topic-selection-slot')) return;
      if (selecting) { event.preventDefault(); event.stopPropagation(); if (eligible) selection.toggle(selectionMessage); }
    },
    },
  };
}
